import { gql, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import type { EntityOrganizationUser } from '@interfaces/user';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { UserContext } from '@providers/userContextProvider';
import { Box, CircularProgress, Typography } from 'hephaestus/web';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import {
  buildSignerRows,
  collectSignerKeys,
  isSignerRowComplete,
  stripDisplayFields,
} from './buildSignerRows';
import type { FormSignerMapping } from './formSignerMapping';
import {
  applyLinks,
  syncLinkedOrders,
  updateSignerRow,
} from './linkSignerRows';
import { SignerRow } from './SignerRow';

type ManageSignersSubStepOptions = {
  subtitle?: TranslatedString;
  editSigningOrder?: boolean;
  autoAssignCurrentUser?: boolean;
};

type SignerUser = {
  firstName?: string;
  lastName?: string;
  email?: string;
  id?: string;
};

const FETCH_SIGNER_TYPES = gql`
  query fetchSignerTypes($input: FetchSignerTypesInput!) {
    fetchSignerTypes(input: $input) {
      signerTypes {
        id
        key
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        isPrimary
        defaultSigningOrder
        roleProfiles {
          id
        }
      }
      totalCount
    }
  }
`;

const FETCH_USER = gql`
  query fetchUser($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        type
        accessibleBy(input: { excludeRepCodeUsers: true }) {
          relation
          organizationUser {
            id
            firstName
            lastName
            email
          }
        }
      }
    }
  }
`;

const FETCH_FORM_COMPLETIONS = gql`
  query fetchFormCompletions($input: FetchFormCompletionsInput!) {
    fetchFormCompletions(input: $input) {
      formCompletions {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        state
        signingType
        requiredSignatures {
          signerKey
          signerUserId
          signerUser {
            id
            firstName
            lastName
            primaryEmail
          }
        }
      }
      totalCount
    }
  }
`;

const FETCH_ROLES = gql`
  query fetchRoles($input: FetchRolesInput!) {
    fetchRoles(input: $input) {
      roles {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        roleProfile {
          id
        }
      }
    }
  }
`;

type RoleWithProfile = {
  id: string;
  translatedName: TranslatedString;
  roleProfile?: {
    id: string;
  };
};

export const ManageSigners = ({
  onNext,
  workflowCompletion,
  userId,
  options,
}: {
  onNext: () => void;
  workflowCompletion: any;
  userId: string;
  options: ManageSignersSubStepOptions;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['workflowCompletions']);
  const { activeOrganization, userContext } = useContext(UserContext);
  const [signerMapping, setSignerMapping] = useState<any[]>([]);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const [signerMappingLoading, setSignerMappingLoading] = useState(true);
  const { rows: displayRows, linkedKeys } = useMemo(
    () => applyLinks(signerMapping),
    [signerMapping],
  );

  const { data: formCompletionsData } = useQuery(FETCH_FORM_COMPLETIONS, {
    variables: {
      input: {
        filter: {
          workflowCompletionId: workflowCompletion.id,
          states: ['UNSIGNED', 'SIGNED'],
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip: !workflowCompletion?.id,
  });

  const { data } = useQuery(FETCH_SIGNER_TYPES, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
        },
        pagination: { perPage: 1000 },
      },
    },
  });

  const { data: userData } = useQuery(FETCH_USER, {
    fetchPolicy: 'no-cache',
    variables: {
      userId,
    },
  });

  const hasRestrictedSigners = signerMapping.some(
    (s: FormSignerMapping) => s.roleProfiles?.length,
  );

  const { data: rolesData } = useQuery(FETCH_ROLES, {
    variables: {
      input: {
        filter: { organizationId: activeOrganization.id },
        pagination: { perPage: 1000 },
      },
    },
    skip: !hasRestrictedSigners,
  });

  const roles: RoleWithProfile[] = rolesData?.fetchRoles?.roles ?? [];

  const getRestrictedRoleNames = (signer: FormSignerMapping): string[] => {
    if (!signer.roleProfiles?.length) return [];
    const profileIds = new Set(signer.roleProfiles.map((p) => p.id));

    return roles
      .filter(
        (role) => role.roleProfile?.id && profileIds.has(role.roleProfile.id),
      )
      .map((role) => translateBackendString(role.translatedName))
      .filter(Boolean);
  };

  useEffect(() => {
    if (
      formCompletionsData?.fetchFormCompletions?.formCompletions &&
      data?.fetchSignerTypes?.signerTypes &&
      userData?.fetchUser?.user
    ) {
      const { formCompletions } = formCompletionsData.fetchFormCompletions;
      const { signerTypes } = data.fetchSignerTypes;
      const signerKeys = collectSignerKeys(formCompletions);
      const currentSignerMapping =
        workflowCompletion.context.signerMapping || [];

      const soleSignerType =
        signerKeys.length === 1
          ? signerTypes.find(
              (type: { key: string }) => type.key === signerKeys[0],
            )
          : undefined;

      // Auto-assign only for a single unrestricted signer; role-profile
      // restrictions must stay on this step for manual selection.
      const autoAssignCurrentUser =
        options.autoAssignCurrentUser &&
        !!soleSignerType &&
        !soleSignerType.roleProfiles?.length;

      let defaultSignerUser: SignerUser | undefined;
      if (autoAssignCurrentUser) {
        const signerUser =
          userData.fetchUser.user.type !== 'INDIVIDUAL'
            ? userData.fetchUser.user.accessibleBy.find(
                (item: EntityOrganizationUser) => !!item.organizationUser,
              )?.organizationUser
            : undefined;

        defaultSignerUser = {
          id: signerUser ? signerUser.id : userContext.id,
          firstName: signerUser ? signerUser.firstName : userContext.firstName,
          lastName: signerUser ? signerUser.lastName : userContext.lastName,
          email: signerUser ? signerUser.email : userContext.email,
        };
      }
      const formSigners = buildSignerRows({
        formCompletions,
        signerTypes,
        savedSignerMapping: currentSignerMapping,
        defaultSignerUser,
        userBoundFallbackLabel: t('manageSigners.clientSigner'),
      });
      const linkedSigners = syncLinkedOrders(formSigners, {
        savedSignerMapping: currentSignerMapping,
        editSigningOrder: options.editSigningOrder,
      });
      setSignerMapping(linkedSigners);

      if (autoAssignCurrentUser && linkedSigners.length === 1) {
        save(linkedSigners);
      } else {
        setSignerMappingLoading(false);
      }
    }
  }, [workflowCompletion, data, userData, formCompletionsData]);

  const updateSigner = (
    signerKey: string,
    changes: Partial<FormSignerMapping>,
  ) =>
    setSignerMapping((current) =>
      updateSignerRow(current, signerKey, changes, {
        savedSignerMapping: workflowCompletion.context.signerMapping || [],
        editSigningOrder: options.editSigningOrder,
      }),
    );

  const save = async (formSigners?: FormSignerMapping[]) => {
    await setWorkflowData({
      ...workflowData,
      signerMapping: applyLinks(formSigners ?? signerMapping).rows.map(
        stripDisplayFields,
      ),
    });
    await onNext();
  };

  if (signerMappingLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          minHeight: '300px',
        }}
      >
        <Typography variant='headingMedium'>
          {t('manageSigners.manageSigners')}
        </Typography>
        <Typography variant='bodyMedium' sx={{ mb: 4, fontWeight: 500 }}>
          {t('manageSigners.description')}
        </Typography>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <WorkflowCompletionForm
      onSubmit={() => save()}
      submitButtonProps={{
        label: t('continue'),
        disabled: !signerMapping.every(isSignerRowComplete),
        dataTestId: 'manage-signers-continue-button',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant='displayLarge' sx={{ mt: 1, mb: 2 }}>
          {t('manageSigners.manageSigners')}
        </Typography>
        {options?.subtitle && (
          <Typography variant='bodyLarge' sx={{ mb: 3 }}>
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackendString(options?.subtitle)}
            </ReactMarkdown>
          </Typography>
        )}
        {displayRows.map((signer: FormSignerMapping) => (
          <SignerRow
            key={signer.signerKey}
            signer={signer}
            linkedOrder={
              linkedKeys.has(signer.signerKey) ? signer.signingOrder : undefined
            }
            editSigningOrder={options.editSigningOrder}
            restrictedRoleNames={getRestrictedRoleNames(signer)}
            rolesLoaded={!!rolesData}
            onChange={(changes) => updateSigner(signer.signerKey, changes)}
          />
        ))}
      </Box>
    </WorkflowCompletionForm>
  );
};
